import { Injectable, OnDestroy } from "@angular/core";
import { NavigationEnd, Router } from "@angular/router";
import { Subscription } from "rxjs";
import { filter } from "rxjs/operators";
import { WsNavigatorService } from "@web/app/services/ws-navigator.service";

export interface AiAgentUiStep {
  url: string;
  title: string;
  subtitle?: string;
  at: string;
}

export interface AiAgentUiContext {
  url: string;
  title: string;
  subtitle?: string;
  breadcrumb: string[];
  /** Oldest → newest (excludes current when duplicated). */
  recentSteps: AiAgentUiStep[];
}

const MAX_STEPS = 8;

/**
 * Tracks where the CRM user is and recent navigations for the AI agent prompt.
 */
@Injectable({ providedIn: "root" })
export class AiAgentUiContextService implements OnDestroy {
  private steps: AiAgentUiStep[] = [];
  private sub?: Subscription;

  constructor(
    private router: Router,
    private navigator: WsNavigatorService
  ) {
    this.recordCurrent();
    this.sub = this.router.events
      .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
      .subscribe(() => this.recordCurrent());
  }

  ngOnDestroy(): void {
    this.sub?.unsubscribe();
  }

  /** Snapshot for the next agent turn (not persisted in chat history). */
  snapshot(): AiAgentUiContext {
    const routerUrl = this.cleanUrl(this.router.url);
    // Full-page agent is not a useful "current page" — prefer last CRM screen.
    const focus = this.isAiAgentUrl(routerUrl)
      ? this.lastCrmStep()
      : null;

    const state = this.navigator.currentState;
    const url = focus?.url || routerUrl;
    const title =
      (focus?.title || "").trim() ||
      (state?.title || "").trim() ||
      this.titleFromUrl(url);
    const subtitle =
      (focus?.subtitle || "").trim() ||
      (state?.subtitle || "").trim() ||
      undefined;
    const breadcrumb = focus
      ? []
      : (state?.breadcrumb || [])
          .map((b) => (b?.label || "").trim())
          .filter(Boolean);

    const recentSteps = this.steps
      .filter((s) => s.url !== url && !this.isAiAgentUrl(s.url))
      .slice(-MAX_STEPS);

    return {
      url,
      title,
      subtitle,
      breadcrumb,
      recentSteps,
    };
  }

  private isAiAgentUrl(url: string): boolean {
    return /\/ai-agent(?:\/|$)/.test(url || "");
  }

  private lastCrmStep(): AiAgentUiStep | null {
    for (let i = this.steps.length - 1; i >= 0; i--) {
      const s = this.steps[i];
      if (s && !this.isAiAgentUrl(s.url)) {
        return s;
      }
    }
    return null;
  }

  private recordCurrent(): void {
    const state = this.navigator.currentState;
    const url = this.cleanUrl(this.router.url);
    if (!url || url === "/") {
      return;
    }
    const title = (state?.title || "").trim() || this.titleFromUrl(url);
    const subtitle = (state?.subtitle || "").trim() || undefined;
    const last = this.steps[this.steps.length - 1];
    if (last && last.url === url) {
      last.title = title;
      last.subtitle = subtitle;
      last.at = new Date().toISOString();
      return;
    }
    this.steps.push({
      url,
      title,
      subtitle,
      at: new Date().toISOString(),
    });
    if (this.steps.length > MAX_STEPS + 2) {
      this.steps = this.steps.slice(-(MAX_STEPS + 2));
    }
  }

  private cleanUrl(url: string): string {
    return (url || "").split("?")[0].split("#")[0] || "";
  }

  private titleFromUrl(url: string): string {
    const seg = url.split("/").filter(Boolean);
    return seg.length ? seg[seg.length - 1] : url;
  }
}
